import { useState } from "react";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { US_STATES } from "../lib/constants";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";

export const alertSummary = (states) => (!states?.length ? "All states" : states.length > 4 ? `${states.slice(0, 4).join(", ")} +${states.length - 4}` : states.join(", "));

export const StatesPicker = ({ value, onSave, trigger, title, note, emptyNote, testid, choices = Object.keys(US_STATES), allLabel = "All states", noneLabel, noneNote, valueNone = false }) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(value || []);
  const [none, setNone] = useState(valueNone);
  const [busy, setBusy] = useState(false);
  const toggle = (s) => { setNone(false); setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s])); };

  const save = async () => {
    setBusy(true);
    try {
      await onSave(picked, none && !picked.length);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) { setPicked(value || []); setNone(valueNone); } }}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-[320px] p-0 bg-white" align="start" collisionPadding={8} data-testid={testid}>
        <div className="p-3 border-b border-slate-100">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <p className="text-xs text-slate-500">{note}</p>
          <p className="text-xs font-semibold text-slate-700" data-testid={`${testid}-summary`}>{picked.length ? `${picked.length} selected` : none ? noneNote : emptyNote}</p>
        </div>
        <div className="grid grid-cols-5 gap-1.5 p-3 max-h-60 overflow-y-auto">
          {choices.map((s) => (
            <button key={s} onClick={() => toggle(s)} title={US_STATES[s]} data-testid={`alert-state-${s}`}
              className={`h-9 rounded-lg text-xs font-semibold border transition-colors ${picked.includes(s) ? "bg-blue-700 border-blue-700 text-white" : "border-slate-200 text-slate-700 hover:border-blue-300"}`}>
              {s}
            </button>
          ))}
        </div>
        <div className="flex gap-2 p-3 border-t border-slate-100">
          <Button variant="ghost" onClick={() => { setPicked([]); setNone(false); }} className={`flex-1 rounded-full ${!picked.length && !none ? "bg-slate-100" : ""}`} data-testid="alert-states-all">{allLabel}</Button>
          {noneLabel && <Button variant="ghost" onClick={() => { setPicked([]); setNone(true); }} className={`flex-1 rounded-full ${none && !picked.length ? "bg-red-50 text-red-700" : ""}`} data-testid="alert-states-none">{noneLabel}</Button>}
          <Button onClick={save} disabled={busy} className="flex-1 rounded-full bg-blue-700 hover:bg-blue-800" data-testid="alert-states-save">Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const AlertStatesPicker = ({ member, onSaved }) => (
  <StatesPicker value={member.alert_states} valueNone={member.alerts_off} testid="alert-states-popover" title="Alert states"
    note="Used for fresh-lead alerts and the morning hot-leads email." emptyNote="All states = alerts for every state"
    noneLabel="No states" noneNote="No states = alerts off"
    onSave={async (states, none) => {
      await api.put(`/team/${member.id}/alert-states`, { states, none });
      toast.success(none ? `${member.name}: alerts are off` : `${member.name}: alerts & morning email for ${alertSummary(states)}`);
      onSaved();
    }}
    trigger={
      <button className="flex items-center gap-1 text-blue-700 hover:underline" data-testid={`team-alert-states-${member.id}`}>
        <BellRing className="h-3 w-3" />Alerts: {member.alerts_off ? "Off" : alertSummary(member.alert_states)}
      </button>
    } />
);
